/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Low-level `?force-error=<componentName>` test hook. Reading the URL param is
 * a pure primitive that `ctx()` consults on every call, so it lives in util
 * (below both debug and the callers). The Debug Controls page (`/debug`) sets
 * the param; `util/context.ts` reads it to schedule a synthetic error.
 */

import { globalRef } from '@amazon/vinyl-util'

/** URL search-param used by {@link getForcedErrorComponent}. */
export const FORCE_ERROR_PARAM = 'force-error'

const enabledRef = globalRef(() => ({ value: false }))

/** `core/debug` enables the hook on debug hosts; util cannot import core, so the flag flows in. */
export function setForcedErrorsEnabled(value: boolean): void {
    enabledRef.value.value = value
}

function readSearchParam(name: string): string | null {
    try {
        if (typeof location === 'undefined') return null
        return new URLSearchParams(location.search).get(name)
    } catch {
        return null
    }
}

/**
 * Returns the current `?force-error=<component>` param, or `null` when
 * the flag is absent / empty or forced errors are not enabled. The value is a component name as passed
 * to `ctx('...')` (e.g. `Feed`, `ChatPage`).
 */
export function getForcedErrorComponent(): string | null {
    if (!enabledRef.value.value) return null
    const raw = readSearchParam(FORCE_ERROR_PARAM)
    return raw != null && raw.length > 0 ? raw : null
}

/**
 * True when `?force-error` is present AND matches `componentName`. Used
 * by `ctx()` to schedule a synthetic error on the next tick.
 */
export function shouldForceErrorFor(componentName: string): boolean {
    return getForcedErrorComponent() === componentName
}
